import { useApolloClient, useLazyQuery } from "@apollo/client";
import { useSnackbarApi } from "@comet/admin";
import { Dialog } from "@mui/material";
import Snackbar from "@src/components/snackbar/Snackbar";
import {
    type GQLCameraByHardwareInfoAdminQuery,
    type GQLPurchaseFragmentFragment,
    type GQLQuerycameraByHardwareInfoAdminArgs,
    type GQLUpdateLicenceCameraInput,
    type GQLUpdateLicenceCameraMutation,
    type GQLUpdateLicenceCameraMutationVariables,
} from "@src/graphql.generated";
import { type FunctionComponent, type PropsWithChildren } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { InputField, ShowField } from "../../fields";
// eslint-disable-next-line @comet/no-private-sibling-import
import { CameraInfoWrapper, ContentWrapper, StyledTypography } from "../Dialog.sc";
import { cameraByHardwareInfoQuery, updateLicenceCameraQuery } from "./ChangeDeviceDialog.gql";
import ChangeDeviceForm from "./ChangeDeviceForm";

interface ReplaceOverview {
    data: GQLCameraByHardwareInfoAdminQuery["cameraByHardwareInfoAdmin"];
    oldCameraHardwareInfo: GQLQuerycameraByHardwareInfoAdminArgs;
}
const ReplaceOverview: FunctionComponent<PropsWithChildren<ReplaceOverview>> = ({ data, oldCameraHardwareInfo }) => {
    const intl = useIntl();

    return (
        <StyledTypography>
            <FormattedMessage id="reviermanager.camera.change.old" defaultMessage="Möchten Sie jetzt das Gerät:" />
            <CameraInfoWrapper>
                <ShowField
                    label={intl.formatMessage({ id: "reviermanager.camera.change.sn", defaultMessage: "SN:" })}
                    value={oldCameraHardwareInfo.sn}
                />
                <ShowField
                    label={intl.formatMessage({ id: "reviermanager.camera.change.msisdn", defaultMessage: "MSISDN:" })}
                    value={oldCameraHardwareInfo.msisdn}
                />
            </CameraInfoWrapper>

            <FormattedMessage id="reviermanager.camera.change.new" defaultMessage="mit dem folgendem Gerät ersetzen?" />
            <CameraInfoWrapper>
                <ShowField label={intl.formatMessage({ id: "reviermanager.camera.change.sn", defaultMessage: "SN:" })} value={data.hardwareInfo.sn} />
                <ShowField
                    label={intl.formatMessage({ id: "reviermanager.camera.change.msisdn", defaultMessage: "MSISDN:" })}
                    value={data.hardwareInfo.msisdn}
                />
            </CameraInfoWrapper>
        </StyledTypography>
    );
};

interface ChangeDeviceDialog {
    open: boolean;
    handleClose: () => void;
    oldCamera: GQLPurchaseFragmentFragment["camera"];
    licenceId: string;
}

const ChangeDeviceDialog: FunctionComponent<PropsWithChildren<ChangeDeviceDialog>> = ({ open, handleClose, oldCamera, licenceId }) => {
    const intl = useIntl();
    const client = useApolloClient();
    const [getCamera, { data }] = useLazyQuery(cameraByHardwareInfoQuery);
    const snackbarApi = useSnackbarApi();

    const submit = ({ cameraId }: GQLUpdateLicenceCameraInput) =>
        client.mutate<GQLUpdateLicenceCameraMutation, GQLUpdateLicenceCameraMutationVariables>({
            mutation: updateLicenceCameraQuery,
            variables: {
                id: licenceId,
                input: {
                    cameraId,
                },
            },
            refetchQueries: ["PurchaseAdmin"],
        });

    const handleSubmit = ({ sn, msisdn }: GQLQuerycameraByHardwareInfoAdminArgs) => {
        if (data) {
            submit({ cameraId: data.cameraByHardwareInfoAdmin.id });
            handleClose();

            snackbarApi.showSnackbar(
                <Snackbar
                    variant="success"
                    message={intl.formatMessage({
                        id: "reviermanager.licence.changeCamera",
                        defaultMessage: "Gerät wurde ersetzt",
                    })}
                    key="reviermanager.licence.changeCamera"
                    handleClose={snackbarApi.hideSnackbar}
                />,
            );
        } else {
            getCamera({ variables: { sn, msisdn } });
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <ContentWrapper>
                <ChangeDeviceForm
                    mode="edit"
                    onSubmit={handleSubmit}
                    title={intl.formatMessage({ id: "reviermanager.camera.change.title", defaultMessage: "Gerät wechseln" })}
                    handleCancelClick={handleClose}
                    handleSubmitClick={() => {
                        handleSubmit(data.cameraByHardwareInfoAdmin.hardwareInfo);
                    }}
                    data={!!data}
                >
                    {data && oldCamera ? (
                        <ReplaceOverview
                            data={data.cameraByHardwareInfoAdmin}
                            oldCameraHardwareInfo={{ sn: oldCamera.hardwareInfo.sn, msisdn: oldCamera.hardwareInfo.msisdn }}
                        />
                    ) : (
                        <>
                            <InputField
                                name="sn"
                                label={intl.formatMessage({ id: "reviermanager.camera.sn", defaultMessage: "SN" })}
                                variant="vertical"
                            />
                            <InputField
                                name="msisdn"
                                label={intl.formatMessage({ id: "reviermanager.camera.msisdn", defaultMessage: "MSISDN" })}
                                variant="vertical"
                            />
                        </>
                    )}
                </ChangeDeviceForm>
            </ContentWrapper>
        </Dialog>
    );
};

export default ChangeDeviceDialog;
